import React, { FunctionComponent } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import classnames from 'classnames';

// components
import { Hold } from './Hold';
import { PrivateOfferItemPrices } from './PrivateOfferItemPrices';
import { SaleItemPrices } from './SaleItemPrices';
import { NetItemPrices } from './NetItemPrices';
import { RetailItemPrices } from './RetailItemPrices';
import { PriceAmount } from './PriceAmount';
import { textPriceValue } from './messages/priceMessages';
import SoldWordIcon from 'dibs-icons/exports/Sold';

import { PRICE_TYPES, TEXT_SIZE_SMALL, TEXT_SIZE_MEDIUM } from './constants';

// helpers
import { getPriceFromAmountListByCurrency, isValidTextOnlyPriceType } from './helpers';

// styles
import styles from './PriceDisplay.scss';

// types
import { PriceDisplay_item as ItemType } from './__generated__/PriceDisplay_item.graphql';
import { PriceDisplay_sku as SkuType } from './__generated__/PriceDisplay_sku.graphql';

type Props = {
    currency?: string;
    item: ItemType;
    sku?: SkuType;
    textSize?: string;
    useSoldIcon?: boolean;
};

export const PriceDisplayComponent: FunctionComponent<Props> = ({
    item,
    sku,
    currency = 'USD',
    textSize = TEXT_SIZE_MEDIUM,
    useSoldIcon = false,
}) => {
    const holdDisplayTime = item?.activeHold?.displayTimeToExpire || null;
    const itemPriceDisplay = sku ? sku?.displayPrice?.[0] : item?.displayPrice?.[0];

    if (!itemPriceDisplay) {
        return <div className={styles.wrapper} />;
    }

    const {
        convertedAmountList,
        originalConvertedAmountList,
        privateOfferConvertedAmountList,
        amountType,
        textType,
        quantityDisplay,
        percentageOff,
        showPriceLabel,
        offerExpirationDate,
    } = itemPriceDisplay;

    const showSold = textType === PRICE_TYPES.SOLD;
    const showHold = textType === PRICE_TYPES.HOLD || textType === PRICE_TYPES.HOLD_CUSTOM;

    const price = getPriceFromAmountListByCurrency(convertedAmountList, currency);
    const originalPrice = getPriceFromAmountListByCurrency(originalConvertedAmountList, currency);
    const percentageValue = percentageOff ? parseInt(percentageOff) : undefined;
    const isPriceValid = typeof price === 'number' && price > 0;

    // Getting priceComponent
    let priceComponent = null;
    const sharedPriceProps = {
        price,
        originalPrice,
        currency,
        quantityDisplay,
        hasPrivateOffer: !!privateOfferConvertedAmountList,
    };

    if (isValidTextOnlyPriceType(textType)) {
        const showPrice = price && textType !== PRICE_TYPES.PUR;

        // Item is unavailable or has no visible price (e.g. sold, unavailable or pur)
        priceComponent = (
            <div className={styles.soldUnavailablePriceWrapper}>
                <div
                    className={classnames({
                        [styles.sold]: showSold,
                        [styles.unavailable]: textType === PRICE_TYPES.UNAVAILABLE,
                        [styles.pur]: textType === PRICE_TYPES.PUR,
                    })}
                    data-tn={`price-${textType}-price`}
                >
                    {showSold && useSoldIcon ? (
                        <SoldWordIcon className={styles.icon} />
                    ) : (
                        textPriceValue(textType)
                    )}
                </div>
                {showPrice && (
                    <PriceAmount
                        type="RETAIL"
                        currency={currency}
                        amount={price}
                        showLabel
                        quantityDisplay={quantityDisplay}
                        isUnavailable
                    />
                )}
            </div>
        );
    } else if (isPriceValid && !isValidTextOnlyPriceType(textType)) {
        if (amountType === PRICE_TYPES.SALE) {
            // Item is on sale
            priceComponent = (
                <SaleItemPrices {...sharedPriceProps} percentageOff={percentageValue} />
            );
        } else if (amountType === PRICE_TYPES.NET) {
            // Item has a net price and is being viewed by a trade user
            priceComponent = (
                <NetItemPrices {...sharedPriceProps} percentageOff={percentageValue} />
            );
        } else if (amountType === PRICE_TYPES.RETAIL) {
            // Item only has a retail price (or is being viewed by a non-trade user)
            priceComponent = (
                <RetailItemPrices {...sharedPriceProps} showPriceLabel={!!showPriceLabel} />
            );
        }
    }

    // Items can have holds, but not valid price e.g. PUR items
    if (!priceComponent && !showHold) {
        return null;
    }

    let privateOffer;
    if (privateOfferConvertedAmountList) {
        const privateOfferPrice = getPriceFromAmountListByCurrency(
            privateOfferConvertedAmountList,
            currency
        );

        privateOffer = (
            <PrivateOfferItemPrices
                {...sharedPriceProps}
                price={privateOfferPrice}
                expiration={offerExpirationDate}
                showExpirationDate={!holdDisplayTime}
            />
        );
    }

    const priceWrapperClasses = classnames(styles.wrapper, {
        [styles.smallText]: textSize === TEXT_SIZE_SMALL,
    });

    return (
        <div className={priceWrapperClasses} data-tn="price-wrapper">
            {/* 'On Hold For You' OR 'On Hold' */}
            {(textType === PRICE_TYPES.HOLD || textType === PRICE_TYPES.HOLD_CUSTOM) && (
                <Hold holdType={textType} holdDisplayTime={holdDisplayTime} />
            )}
            {/* Price */}
            {priceComponent}
            {/* show Private Offer if exists */}
            {privateOffer}
        </div>
    );
};

export const PriceDisplay = createFragmentContainer(PriceDisplayComponent, {
    item: graphql`
        fragment PriceDisplay_item on Item
        @argumentDefinitions(
            hasUserId: { type: "Boolean", defaultValue: false }
            userId: { type: "String!", defaultValue: "" }
            isTrade: { type: "Boolean", defaultValue: false }
        ) {
            activeHold {
                displayTimeToExpire
            }
            displayPrice(isTrade: $isTrade, buyerId: $userId) {
                originalConvertedAmountList {
                    currency
                    amount
                }
                convertedAmountList {
                    currency
                    amount
                }
                privateOfferConvertedAmountList {
                    currency
                    amount
                }
                quantityDisplay
                percentageOff
                textType
                amountType
                showPriceLabel
                showPriceVariability
                offerExpirationDate
            }
        }
    `,
    sku: graphql`
        fragment PriceDisplay_sku on ItemSkuType
        @argumentDefinitions(
            hasSkuId: { type: "Boolean", defaultValue: false }
            hasUserId: { type: "Boolean", defaultValue: false }
            userId: { type: "String!", defaultValue: "" }
            isTrade: { type: "Boolean", defaultValue: false }
        ) {
            ... @include(if: $hasSkuId) {
                serviceId
                displayPrice(isTrade: $isTrade, buyerId: $userId) {
                    originalConvertedAmountList {
                        currency
                        amount
                    }
                    convertedAmountList {
                        currency
                        amount
                    }
                    privateOfferConvertedAmountList {
                        currency
                        amount
                    }
                    quantityDisplay
                    percentageOff
                    textType
                    amountType
                    showPriceLabel
                    showPriceVariability
                    offerExpirationDate
                }
            }
        }
    `,
});
